import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext();

const TOKEN_KEY = 'oe_access_token';
const REFRESH_KEY = 'oe_refresh_token';

// Send cookies with every request app-wide (cookie auth is still the primary path)
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const interceptorInstalled = useRef(false);

  useEffect(() => {
    if (!interceptorInstalled.current) {
      interceptorInstalled.current = true;

      // Request interceptor: always attach Bearer token fallback if present in localStorage
      axios.interceptors.request.use((config) => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token && config.url && config.url.startsWith(API)) {
          config.headers = config.headers || {};
          if (!config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      });

      // Response interceptor: on 401, try to refresh via cookie OR X-Refresh-Token header, then retry
      axios.interceptors.response.use(
        (res) => res,
        async (error) => {
          const original = error.config || {};
          const url = original.url || '';
          const status = error.response?.status;
          const isAuthEndpoint =
            url.includes('/api/auth/login') ||
            url.includes('/api/auth/refresh') ||
            url.includes('/api/auth/me');

          if (status === 401 && !original._retried && !isAuthEndpoint) {
            original._retried = true;
            try {
              const storedRefresh = localStorage.getItem(REFRESH_KEY);
              const refreshHeaders = storedRefresh ? { 'X-Refresh-Token': storedRefresh } : {};
              const { data } = await axios.post(
                `${API}/api/auth/refresh`,
                {},
                { withCredentials: true, headers: refreshHeaders }
              );
              if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
              // Replace Authorization on original with the new token
              const newTok = data.access_token || localStorage.getItem(TOKEN_KEY);
              if (newTok) {
                original.headers = original.headers || {};
                original.headers.Authorization = `Bearer ${newTok}`;
              }
              return axios(original);
            } catch (refreshErr) {
              if (refreshErr.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(REFRESH_KEY);
                setUser(null);
                if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin/login')) {
                  window.location.href = '/admin/login';
                }
              }
              return Promise.reject(refreshErr);
            }
          }
          return Promise.reject(error);
        }
      );
    }

    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(res => { setUser(res.data); setChecking(false); })
      .catch(() => { setUser(null); setChecking(false); });

    // If user opted out of "Remember me", clear tokens when the tab/window is closed
    const handleUnload = () => {
      if (sessionStorage.getItem('oe_session_only') === '1') {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
    if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };  const logout = async () => {
    try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); } catch (_) {}
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
